import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { getPartnerDetail, updateUserStatus } from '../../../services/memberService';
import { getPartnerDashboardStats } from '../../../services/partnerDashboardService';
import {
  formatBusinessNumber,
  formatDateDigits,
} from '../../../services/partnerLicenseVerification';

const STATUS_LABEL = { active: '활성화', pending: '승인 대기', suspended: '정지', inactive: '비활성화' };

// ───── 스타일 ─────
const Wrap = styled.div``;

const BackBtn = styled.button`
  background: none; border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px; cursor: pointer; margin-bottom: 12px; padding: 0;
  &:hover { text-decoration: underline; }
`;

const TopRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px;
`;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const RefreshBtn = styled.button`
  background: none; border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px; cursor: pointer;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
`;

const StatCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 16px 18px;
`;
const StatLabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;
const StatValue = styled.div`
  font-size: 20px; font-weight: 600;
  color: ${({ $c }) => $c || '#1F2937'};
`;

const SubTitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: -10px;
  margin-bottom: 14px;
`;

const DataTable = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;
const DataRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  min-height: 38px;
  align-items: center;
`;
const DataLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 8px 12px;
  background: #FAFAFA;
  font-weight: 500;
  align-self: stretch;
  display: flex;
  align-items: center;
`;
const DataValue = styled.div`
  font-size: 13px;
  color: #1F2937;
  padding: 8px 14px;
  word-break: break-all;
`;
const StatusDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $color }) => $color || '#9CA3AF'};
  margin-right: 6px;
  vertical-align: middle;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 20px 22px;
`;
const CardTitle = styled.h3`
  font-size: 14px; font-weight: 600; margin-bottom: 16px;
  color: #1F2937;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 3}, 1fr);
  gap: 14px 28px;
  @media (max-width: 768px) { grid-template-columns: 1fr 1fr; }
`;

const InfoItem = styled.div``;
const InfoLabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 4px;
`;
const InfoValue = styled.div`
  font-size: 13px; font-weight: 500;
  color: #1F2937;
  word-break: break-all;
`;

const Muted = styled.span`color:#9CA3AF;font-style:italic;`;

const StatusSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 13px;
  background: #fff;
`;

const PipelineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
`;
const PipelineBox = styled.div`
  text-align: center;
  padding: 14px 8px;
  background: #F9FAFB;
  border-radius: 8px;
`;
const PipelineCount = styled.div`
  font-size: 18px; font-weight: 600;
  color: ${({ $c, theme }) => $c || theme.colors.primary};
`;
const PipelineLabel = styled.div`
  font-size: 12px; color: #6B7280; margin-top: 4px;
`;

const formatDate = (v) => {
  if (!v) return '-';
  if (typeof v === 'string') return v.slice(0, 16);
  try {
    let d;
    if (v.toDate) d = v.toDate();
    else if (v.seconds) d = new Date(v.seconds * 1000);
    else d = new Date(v);
    if (isNaN(d.getTime())) return '-';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return '-'; }
};
const formatWon = (n) => `₩${(n || 0).toLocaleString()}`;

// 자동검증 결과 → 경고 뱃지 (폐업/미등록만 표시, 통과는 안 보임)
const NTS_WARNING = ({ data }) => {
  const st = data.auto_verification_status;
  if (!st || st === 'ocr_auto_approved' || st === 'ocr_processing' || st === 'manual_pending') return null;
  const text = data.nts_b_stt && data.nts_b_stt !== '계속사업자'
    ? `⚠ ${data.nts_b_stt}`
    : st === 'ocr_auto_rejected' ? '⚠ 국세청 검증 실패'
    : st === 'ocr_review_required' ? '⚠ 확인 필요'
    : '';
  if (!text) return null;
  return (
    <span style={{
      marginLeft: 8,
      fontSize: 11,
      padding: '2px 8px',
      borderRadius: 999,
      background: st === 'ocr_auto_rejected' ? '#FEE2E2' : '#FEF3C7',
      color: st === 'ocr_auto_rejected' ? '#DC2626' : '#D97706',
      fontWeight: 600,
    }}>
      {text}
    </span>
  );
};

// 사업자번호 가운데 2자리로 개인/법인 판별 (80번대 이상이 법인계열)
const detectBusinessType = (bizNo) => {
  const d = String(bizNo || '').replace(/\D/g, '');
  if (d.length !== 10) return '';
  const mid = parseInt(d.slice(3, 5), 10);
  if (mid >= 80) return '법인';
  return '개인';
};

const PIPELINE_CARDS = [
  { key: 'pre_consult', label: '상담전', color: '#9CA3AF' },
  { key: 'in_progress', label: '진행중', color: '#3B82F6' },
  { key: 'consulting', label: '상담중', color: '#F97316' },
  { key: 'visiting', label: '방문상담', color: '#8B5CF6' },
  { key: 'reserved', label: '예약완료', color: '#22C55E' },
  { key: 'absent', label: '부재중', color: '#F59E0B' },
  { key: 'pending', label: '보류', color: '#F59E0B' },
  { key: 'completed', label: '완료', color: '#22C55E' },
  { key: 'cancelled', label: '취소', color: '#EF4444' },
];

const PartnerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [mileage, setMileage] = useState({ balance: 0, earned: 0, used: 0 });
  const [sourceStats, setSourceStats] = useState({ purchased: 0, assigned: 0, other: 0, purchaseTotal: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await getPartnerDetail(id);
      setData(detail);
      const s = await getPartnerDashboardStats(id);
      setStats(s);
      // 마일리지 로그 집계
      try {
        const logsSnap = await getDocs(query(collection(db, 'partner_mileage_logs'), where('partner_id', '==', id)));
        let earned = 0; let used = 0;
        logsSnap.docs.forEach((d) => {
          const v = d.data();
          if ((v.amount || 0) > 0) earned += v.amount;
          else used += Math.abs(v.amount || 0);
        });
        setMileage({ balance: earned - used, earned, used });
      } catch { /* skip */ }
      // partner_assignments — 구매/배정 구분 집계
      try {
        const asgnSnap = await getDocs(query(collection(db, 'partner_assignments'), where('partner_id', '==', id)));
        let purchased = 0; let assigned = 0; let other = 0; let purchaseTotal = 0;
        asgnSnap.docs.forEach((d) => {
          const a = d.data();
          const src = a.source || '';
          if (src === 'purchased') { purchased += 1; purchaseTotal += Number(a.price || 0); }
          else if (src === 'assigned') assigned += 1;
          else other += 1;
        });
        setSourceStats({ purchased, assigned, other, purchaseTotal });
      } catch { /* skip */ }
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (status) => {
    if (!window.confirm(`상태를 "${STATUS_LABEL[status]}" 으로 변경하시겠습니까?`)) return;
    try {
      await updateUserStatus(id, status);
      setData((prev) => {
        const next = { ...prev, status };
        if (status === 'active' && !prev?.approved_at) {
          next.approved_at = { seconds: Math.floor(Date.now() / 1000) };
        }
        return next;
      });
    } catch (err) { alert(err.message || '변경 실패'); }
  };

  if (loading) return <Wrap><p>로딩 중...</p></Wrap>;
  if (!data) return <Wrap><p>업체 정보를 찾을 수 없습니다.</p></Wrap>;

  const pipeline = stats?.pipelineCounts || {};
  const totalAssigned = Object.values(pipeline).reduce((a, b) => a + b, 0);

  return (
    <Wrap>
      <BackBtn onClick={() => navigate('/admin/members/partners')}>← 목록으로</BackBtn>

      <TopRow>
        <Title>{data.business_name || data.name || '업체 상세'}</Title>
        <RefreshBtn onClick={load}>새로고침</RefreshBtn>
      </TopRow>

      {/* 4 분할 요약 */}
      <StatGrid>
        <StatCard>
          <StatLabel>마일리지 잔액</StatLabel>
          <StatValue $c="#4A6CF7">{formatWon(mileage.balance)}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>미수금</StatLabel>
          <StatValue $c="#F97316">{formatWon(stats?.receivableTotal || 0)}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>총 배정 건수</StatLabel>
          <StatValue>{totalAssigned}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>DB 열람 클릭수</StatLabel>
          <StatValue $c="#9CA3AF">-</StatValue>
        </StatCard>
      </StatGrid>

      {/* 기본 정보 — 사업자 정보 + 담당자 정보 통합 */}
      <Card style={{ marginBottom: 20 }}>
        <CardTitle>기본 정보</CardTitle>
        <SubTitle>사업자 정보와 담당자 정보를 표준 카드로 표시합니다.</SubTitle>
        <DataTable>
          <DataRow>
            <DataLabel>업체명</DataLabel>
            <DataValue>{data.ocr_business_name || data.business_name || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>대표자</DataLabel>
            <DataValue>{data.ocr_representative_name || data.owner_name || data.name || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>사업자번호</DataLabel>
            <DataValue>
              {formatBusinessNumber(data.ocr_business_number || data.business_number || data.biz_number) || '-'}
              <NTS_WARNING data={data} />
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>사업자 유형</DataLabel>
            <DataValue>
              {(() => {
                const t = data.ocr_business_type || data.business_type;
                if (t === 'corporation') return '법인';
                if (t === 'sole_proprietor') return '개인';
                return detectBusinessType(data.ocr_business_number || data.business_number) || '-';
              })()}
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>개업일자</DataLabel>
            <DataValue>{(data.ocr_open_date || data.open_date) ? formatDateDigits(data.ocr_open_date || data.open_date) : '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>주소</DataLabel>
            <DataValue>{data.ocr_address || data.address || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>대표 연락처</DataLabel>
            <DataValue>{data.business_phone || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>담당자</DataLabel>
            <DataValue>{data.name || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>담당자 연락처</DataLabel>
            <DataValue>{data.manager_phone || data.phone || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>담당자 이메일</DataLabel>
            <DataValue>{data.email || '-'}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>사업자등록증</DataLabel>
            <DataValue>
              {(data.business_license_url || data.license_url) ? (
                <a
                  href={data.business_license_url || data.license_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#4A6CF7' }}
                >
                  {data.license_file_name || '파일 보기'} ↗
                </a>
              ) : '-'}
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>가입승인 일자</DataLabel>
            <DataValue>{formatDate(data.approved_at) !== '-' ? formatDate(data.approved_at) : <Muted>미승인</Muted>}</DataValue>
          </DataRow>
          <DataRow>
            <DataLabel>상태</DataLabel>
            <DataValue>
              <StatusDot $color={
                data.status === 'active' ? '#22C55E'
                : data.status === 'pending' ? '#F59E0B'
                : data.status === 'suspended' ? '#EF4444'
                : '#9CA3AF'
              } />
              <StatusSelect value={data.status || 'inactive'} onChange={(e) => handleStatusChange(e.target.value)}>
                <option value="active">활성화</option>
                <option value="pending">승인 대기</option>
                <option value="suspended">정지</option>
                <option value="inactive">비활성화</option>
              </StatusSelect>
            </DataValue>
          </DataRow>
        </DataTable>
      </Card>

      {/* 마일리지 */}
      <Card style={{ marginBottom: 20 }}>
        <CardTitle>마일리지 현황</CardTitle>
        <InfoGrid $cols={3}>
          <InfoItem><InfoLabel>현재 잔액</InfoLabel><InfoValue>{formatWon(mileage.balance)}</InfoValue></InfoItem>
          <InfoItem><InfoLabel>총 적립액</InfoLabel><InfoValue style={{ color: '#22C55E' }}>{formatWon(mileage.earned)}</InfoValue></InfoItem>
          <InfoItem><InfoLabel>총 사용액</InfoLabel><InfoValue style={{ color: '#EF4444' }}>{formatWon(mileage.used)}</InfoValue></InfoItem>
        </InfoGrid>
      </Card>

      {/* DB 구매/배정 구분 */}
      <Card style={{ marginBottom: 20 }}>
        <CardTitle>DB 획득 경로</CardTitle>
        <InfoGrid $cols={4}>
          <InfoItem>
            <InfoLabel>구매 DB</InfoLabel>
            <InfoValue style={{ color: '#4A6CF7' }}>{sourceStats.purchased} 건</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>구매 총액</InfoLabel>
            <InfoValue style={{ color: '#4A6CF7' }}>{formatWon(sourceStats.purchaseTotal)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>본사 배정 DB</InfoLabel>
            <InfoValue style={{ color: '#22C55E' }}>{sourceStats.assigned} 건</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>기타</InfoLabel>
            <InfoValue style={{ color: '#9CA3AF' }}>{sourceStats.other} 건</InfoValue>
          </InfoItem>
        </InfoGrid>
      </Card>

      {/* DB 배정 현황 (10분할) */}
      <Card>
        <CardTitle>DB 배정 현황 (전체 누적)</CardTitle>
        <PipelineGrid>
          <PipelineBox>
            <PipelineCount>{totalAssigned}</PipelineCount>
            <PipelineLabel>전체</PipelineLabel>
          </PipelineBox>
          {PIPELINE_CARDS.map((p) => (
            <PipelineBox key={p.key}>
              <PipelineCount $c={p.color}>{pipeline[p.key] || 0}</PipelineCount>
              <PipelineLabel>{p.label}</PipelineLabel>
            </PipelineBox>
          ))}
        </PipelineGrid>
      </Card>
    </Wrap>
  );
};

export default PartnerDetail;
